// The "Company appearance" section ultimately owns the company logo upload
// — that same `companies.logo_url` is what the Overview Dashboard renders as
// the per-program card watermark. Wire the upload here when it ships, and the
// dashboard cards will pick it up via listMyCompetitions automatically.

// TODO: full content menu for Settings is undecided. Candidates from spec:
//   - Email preferences
//   - Default judging rubric template
//   - Time zone
//   - Coravu RM contact (if applicable)
//   - Billing (Licensing only)
//   - Audit log access
// Revisit when product decides.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Image,
  Modal as RNModal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import Svg, { Circle, Line, Rect } from "react-native-svg";

import { MfaToggleCard } from "../../../components/security/MfaToggleCard";
import { COLORS, FONTS } from "../../../lib/brand";
import { LogoMark } from "../../../components/brand/Logo";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import { getCompanyById, getMyCompanies, updateCompany } from "../../../lib/companies";
import { pickLogoFile, uploadCompanyLogo } from "../../../lib/companyUploads";
import { router } from "expo-router";
import type { Href } from "expo-router";
import type { Company } from "../../../types/database";

const THEME = PANE_THEMES.Client;

const FOREST = THEME.sidebarBg;
const FOREST_MID = COLORS.FOREST_MID.hex;
const AMBER = THEME.accent;
const SAGE = COLORS.SAGE.hex;
const LINEN = THEME.textOnSurface;
const RED = THEME.destructive;

const W = "rgba(255,255,255,1)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";

const SECTION_MAX_W = 720;

export default function ClientSettingsScreen() {
  const [company, setCompany] = useState<Company | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getMyCompanies().then((list) => {
      if (!cancelled) setCompany(list[0] ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const refetchCompany = useCallback(async () => {
    if (!company?.id) {
      const list = await getMyCompanies();
      setCompany(list[0] ?? null);
      return;
    }
    const fresh = await getCompanyById(company.id);
    if (fresh) setCompany(fresh);
  }, [company?.id]);

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }}>
      <View style={{ paddingHorizontal: 32, paddingVertical: 28, maxWidth: SECTION_MAX_W }}>
        {/* Hero */}
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.displaySemiBold,
              fontSize: 10,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Overview
          </Text>
        </View>
        <Text
          style={{
            marginTop: 8,
            fontFamily: FONTS.displayLight,
            fontSize: 30,
            lineHeight: 36,
            color: LINEN,
          }}
        >
          Settings
        </Text>
        <Text
          style={{
            marginTop: 6,
            fontFamily: FONTS.bodyLight,
            fontSize: 13,
            lineHeight: 20,
            color: WM,
            maxWidth: 600,
          }}
        >
          Manage how you sign in, how Coravu reaches you, and how your company appears.
        </Text>

        {/* Sections */}
        <View style={{ marginTop: 28, gap: 22 }}>
          <SettingsSection
            overline="Security"
            subtitle="Sign-in & two-factor"
          >
            <MfaToggleCard theme="dark" />
          </SettingsSection>

          <SettingsSection
            overline="Notifications"
            subtitle="How Coravu reaches you"
          >
            <NotificationsStub />
          </SettingsSection>

          <SettingsSection
            overline="Company"
            subtitle="Your company appearance"
          >
            <CompanyAppearance company={company} onCompanyChange={refetchCompany} />
          </SettingsSection>

          <SettingsSection overline="Help" subtitle="Tour & guides">
            <HelpRow />
          </SettingsSection>
        </View>
      </View>
    </ScrollView>
  );
}

/* ─────────────────────────  Section shell  ───────────────────────── */

function SettingsSection({
  overline,
  subtitle,
  children,
}: {
  overline: string;
  subtitle: string;
  children: ReactNode;
}) {
  return (
    <View>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 2,
          textTransform: "uppercase",
          color: AMBER,
          marginBottom: 4,
        }}
      >
        {overline}
      </Text>
      <Text
        style={{
          fontFamily: FONTS.displayMedium,
          fontSize: 22,
          lineHeight: 26,
          color: W,
          marginBottom: 14,
        }}
      >
        {subtitle}
      </Text>
      {children}
    </View>
  );
}

/* ─────────────────────────  Notifications stub  ───────────────────────── */

type NotifKey = "email_digests" | "submission_alerts" | "mention_notifications";

const NOTIF_ROWS: Array<{ key: NotifKey; label: string; description: string; defaultOn: boolean }> = [
  {
    key: "email_digests",
    label: "Email digests",
    description: "Weekly summary of your programs",
    defaultOn: true,
  },
  {
    key: "submission_alerts",
    label: "New submission alerts",
    description: "When students submit deliverables",
    defaultOn: true,
  },
  {
    key: "mention_notifications",
    label: "Mention notifications",
    description: "When someone @-mentions you in a comment",
    defaultOn: false,
  },
];

function NotificationsStub() {
  // TODO: wire to user_notification_preferences table when it exists. For now
  // toggles are visual-only (local state) and reset on remount.
  const [state, setState] = useState<Record<NotifKey, boolean>>(() =>
    NOTIF_ROWS.reduce(
      (acc, r) => {
        acc[r.key] = r.defaultOn;
        return acc;
      },
      {} as Record<NotifKey, boolean>
    )
  );

  return (
    <View
      style={{
        backgroundColor: FOREST,
        borderWidth: 1,
        borderColor: BORDER,
        borderRadius: 6,
        overflow: "hidden",
      }}
    >
      {NOTIF_ROWS.map((row, idx) => (
        <View
          key={row.key}
          style={{
            paddingHorizontal: 18,
            paddingVertical: 16,
            flexDirection: "row",
            alignItems: "center",
            gap: 16,
            borderTopWidth: idx === 0 ? 0 : 1,
            borderTopColor: BORDER,
          }}
        >
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13.5, color: LINEN }}>
              {row.label}
            </Text>
            <Text
              style={{
                marginTop: 2,
                fontFamily: FONTS.body,
                fontSize: 11.5,
                color: WM,
              }}
            >
              {row.description}
            </Text>
          </View>
          <Toggle
            value={state[row.key]}
            onValueChange={(v) => setState((s) => ({ ...s, [row.key]: v }))}
          />
        </View>
      ))}
    </View>
  );
}

function Toggle({
  value,
  onValueChange,
}: {
  value: boolean;
  onValueChange: (v: boolean) => void;
}) {
  return (
    <Pressable
      onPress={() => onValueChange(!value)}
      accessibilityRole="switch"
      accessibilityState={{ checked: value }}
      style={[
        {
          width: 38,
          height: 22,
          borderRadius: 999,
          padding: 2,
          backgroundColor: value ? "rgba(143,187,138,0.45)" : "rgba(255,255,255,0.10)",
          borderWidth: 1,
          borderColor: value ? SAGE : BORDER,
          justifyContent: "center",
        } as ViewStyle,
        Platform.OS === "web"
          ? ({ cursor: "pointer", transition: "background-color 150ms" } as object)
          : null,
      ]}
    >
      <View
        style={{
          width: 16,
          height: 16,
          borderRadius: 999,
          backgroundColor: value ? LINEN : "rgba(255,255,255,0.65)",
          alignSelf: value ? "flex-end" : "flex-start",
          ...(Platform.OS === "web" ? ({ transition: "all 150ms" } as object) : {}),
        }}
      />
    </Pressable>
  );
}

/* ─────────────────────────  Company appearance  ───────────────────────── */

function CompanyAppearance({
  company,
  onCompanyChange,
}: {
  company: Company | null;
  onCompanyChange: () => Promise<void> | void;
}) {
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoErr, setLogoErr] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  return (
    <View
      style={{
        backgroundColor: FOREST,
        borderWidth: 1,
        borderColor: BORDER,
        borderRadius: 6,
        padding: 18,
      }}
    >
      {/* Logo tile + Replace logo */}
      <View style={{ flexDirection: "row", alignItems: "center", gap: 18 }}>
        <LogoTile logoUrl={company?.logo_url ?? null} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13.5, color: LINEN }}>
            Company logo
          </Text>
          <Text
            style={{
              marginTop: 2,
              fontFamily: FONTS.body,
              fontSize: 11.5,
              color: WM,
              maxWidth: 360,
            }}
          >
            Used as the watermark behind every program card on your dashboard.
          </Text>
          <Pressable
            disabled={logoBusy || !company?.id}
            onPress={async () => {
              if (!company?.id) return;
              setLogoBusy(true);
              setLogoErr(null);
              try {
                const file = await pickLogoFile();
                if (!file) {
                  setLogoBusy(false);
                  return;
                }
                await uploadCompanyLogo(company.id, file);
                await onCompanyChange();
              } catch (e) {
                setLogoErr(e instanceof Error ? e.message : "Upload failed.");
              } finally {
                setLogoBusy(false);
              }
            }}
            style={[
              { marginTop: 10, alignSelf: "flex-start", opacity: logoBusy ? 0.5 : 1 },
              Platform.OS === "web" ? ({ cursor: "pointer" } as unknown as ViewStyle) : null,
            ]}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 11,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: AMBER,
              }}
            >
              {logoBusy ? "Uploading…" : "Replace logo"}
            </Text>
          </Pressable>
          {logoErr ? (
            <Text
              style={{
                marginTop: 6,
                fontFamily: FONTS.body,
                fontSize: 11,
                color: RED,
              }}
            >
              {logoErr}
            </Text>
          ) : null}
        </View>
      </View>

      {/* Read-only fields */}
      <View style={{ marginTop: 18, gap: 10 }}>
        <ReadOnlyField label="Company name" value={company?.name ?? "—"} />
        <ReadOnlyField label="Public website" value={company?.website ?? "—"} />
        <ReadOnlyField label="Tagline" value={company?.tagline ?? "—"} />
        <ReadOnlyField label="Description" value={company?.description ?? "—"} multiline />
      </View>

      <Pressable
        disabled={!company}
        onPress={() => setEditOpen(true)}
        style={[
          { marginTop: 14, alignSelf: "flex-start", opacity: company ? 1 : 0.4 },
          Platform.OS === "web" ? ({ cursor: "pointer" } as unknown as ViewStyle) : null,
        ]}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 11,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: AMBER,
          }}
        >
          Edit →
        </Text>
      </Pressable>

      {company ? (
        <CompanyInfoEditModal
          visible={editOpen}
          company={company}
          onClose={() => setEditOpen(false)}
          onSaved={async () => {
            setEditOpen(false);
            await onCompanyChange();
          }}
        />
      ) : null}
    </View>
  );
}

function LogoTile({ logoUrl }: { logoUrl: string | null }) {
  if (logoUrl) {
    return (
      <View
        style={{
          width: 64,
          height: 64,
          borderRadius: 6,
          borderWidth: 1,
          borderColor: BORDER,
          backgroundColor: FOREST_MID,
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Image
          source={{ uri: logoUrl }}
          style={{ width: 56, height: 56 }}
          resizeMode="contain"
        />
      </View>
    );
  }
  // Empty state — dashed amber square with the Coravu mark inside (matches
  // the dashboard fallback so the user knows what shows up if they skip it).
  return (
    <View
      style={{
        width: 64,
        height: 64,
        borderRadius: 6,
        borderWidth: 1.5,
        borderStyle: "dashed",
        borderColor: AMBER,
        backgroundColor: "transparent",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CoravuMark size={36} />
    </View>
  );
}

function CoravuMark({ size }: { size: number }) {
  return (
    <LogoMark size={size} ringOuter={SAGE} ringInner={AMBER} dot={AMBER} />
  );
}

function ReadOnlyField({
  label,
  value,
  multiline,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) {
  return (
    <View>
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 9.5,
          letterSpacing: 1.6,
          textTransform: "uppercase",
          color: WM,
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          marginTop: 4,
          fontFamily: FONTS.bodyMedium,
          fontSize: 13,
          color: LINEN,
        }}
        numberOfLines={multiline ? 3 : 1}
      >
        {value}
      </Text>
    </View>
  );
}

void WL;

/* ─────────────────────────  Edit modal  ───────────────────────── */

const TAGLINE_MAX = 120;
const DESCRIPTION_MAX = 500;

function normalizeUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const u = new URL(withScheme);
    if (!u.hostname.includes(".")) return null;
    return u.toString();
  } catch {
    return null;
  }
}

function CompanyInfoEditModal({
  visible,
  company,
  onClose,
  onSaved,
}: {
  visible: boolean;
  company: Company;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [name, setName] = useState(company.name ?? "");
  const [website, setWebsite] = useState(company.website ?? "");
  const [tagline, setTagline] = useState(company.tagline ?? "");
  const [description, setDescription] = useState(company.description ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Re-prime form when the modal opens or the target company changes.
  const lastKeyRef = useRef<string | null>(null);
  if (visible) {
    const key = `${company.id}:${company.name ?? ""}:${company.website ?? ""}:${company.tagline ?? ""}:${company.description ?? ""}`;
    if (lastKeyRef.current !== key) {
      lastKeyRef.current = key;
      setName(company.name ?? "");
      setWebsite(company.website ?? "");
      setTagline(company.tagline ?? "");
      setDescription(company.description ?? "");
      setErr(null);
    }
  } else if (lastKeyRef.current !== null) {
    lastKeyRef.current = null;
  }

  const dirty = useMemo(() => {
    return (
      name.trim() !== (company.name ?? "").trim() ||
      website.trim() !== (company.website ?? "").trim() ||
      tagline.trim() !== (company.tagline ?? "").trim() ||
      description.trim() !== (company.description ?? "").trim()
    );
  }, [name, website, tagline, description, company]);

  function handleCancel() {
    if (!dirty) {
      onClose();
      return;
    }
    const confirmed =
      Platform.OS === "web"
        ? typeof window !== "undefined" && window.confirm("Discard your unsaved changes?")
        : true;
    if (confirmed) onClose();
  }

  async function handleSave() {
    setErr(null);
    const cleanName = name.trim();
    if (!cleanName) {
      setErr("Company name is required.");
      return;
    }
    let websiteValue: string | null = null;
    const rawWebsite = website.trim();
    if (rawWebsite) {
      const normalized = normalizeUrl(rawWebsite);
      if (!normalized) {
        setErr("Enter a valid website URL (e.g. https://example.com).");
        return;
      }
      websiteValue = normalized;
    }
    if (tagline.length > TAGLINE_MAX) {
      setErr(`Tagline must be ${TAGLINE_MAX} characters or fewer.`);
      return;
    }
    if (description.length > DESCRIPTION_MAX) {
      setErr(`Description must be ${DESCRIPTION_MAX} characters or fewer.`);
      return;
    }

    setSaving(true);
    const { error } = await updateCompany(company.id, {
      name: cleanName,
      website: websiteValue ?? "",
      tagline,
      description,
    });
    setSaving(false);
    if (error) {
      setErr(error.message);
      return;
    }
    await onSaved();
  }

  return (
    <RNModal visible={visible} transparent animationType="fade" onRequestClose={handleCancel}>
      <View
        style={{
          flex: 1,
          backgroundColor: "rgba(0,0,0,0.65)",
          justifyContent: "center",
          paddingHorizontal: 16,
        }}
      >
        <View
          style={{
            maxWidth: 560,
            width: "100%",
            maxHeight: "92%",
            alignSelf: "center",
            backgroundColor: FOREST_MID,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: BORDER,
          }}
        >
          <ScrollView contentContainerStyle={{ padding: 22 }}>
            <Text
              style={{
                fontFamily: FONTS.displayMedium,
                fontSize: 22,
                color: LINEN,
              }}
            >
              Edit company info
            </Text>
            <Text
              style={{
                marginTop: 4,
                fontFamily: FONTS.body,
                fontSize: 12,
                color: WM,
              }}
            >
              These values show in the dashboard watermark and in transactional emails.
            </Text>

            {err ? (
              <View
                style={{
                  marginTop: 14,
                  paddingVertical: 10,
                  paddingHorizontal: 12,
                  borderRadius: 6,
                  backgroundColor: "rgba(200,101,75,0.12)",
                  borderWidth: 1,
                  borderColor: "rgba(200,101,75,0.45)",
                }}
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyMedium,
                    fontSize: 12,
                    color: RED,
                  }}
                >
                  {err}
                </Text>
              </View>
            ) : null}

            <View style={{ marginTop: 18, gap: 14 }}>
              <FieldLabel label="Company name" required />
              <DarkInput value={name} onChangeText={setName} placeholder="Acme Corp" />

              <FieldLabel label="Public website" />
              <DarkInput
                value={website}
                onChangeText={setWebsite}
                placeholder="https://acme.com"
                autoCapitalize="none"
                keyboardType="url"
              />

              <View>
                <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
                  <FieldLabel label="Tagline" />
                  <Text style={{ fontFamily: FONTS.body, fontSize: 10.5, color: WM }}>
                    {tagline.length}/{TAGLINE_MAX}
                  </Text>
                </View>
                <DarkInput
                  value={tagline}
                  onChangeText={(t) => setTagline(t.slice(0, TAGLINE_MAX))}
                  placeholder="One-line description shown on case cards"
                />
              </View>

              <View>
                <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
                  <FieldLabel label="Description" />
                  <Text style={{ fontFamily: FONTS.body, fontSize: 10.5, color: WM }}>
                    {description.length}/{DESCRIPTION_MAX}
                  </Text>
                </View>
                <DarkInput
                  value={description}
                  onChangeText={(t) => setDescription(t.slice(0, DESCRIPTION_MAX))}
                  placeholder="A short paragraph students see when they discover your case"
                  multiline
                  numberOfLines={5}
                />
              </View>
            </View>

            <View
              style={{
                marginTop: 22,
                flexDirection: "row",
                justifyContent: "flex-end",
                gap: 10,
              }}
            >
              <SecondaryButton label="Cancel" onPress={handleCancel} disabled={saving} />
              <PrimaryButton
                label={saving ? "Saving…" : "Save"}
                onPress={handleSave}
                disabled={saving}
              />
            </View>
          </ScrollView>
        </View>
      </View>
    </RNModal>
  );
}

function FieldLabel({ label, required }: { label: string; required?: boolean }) {
  return (
    <Text
      style={{
        fontFamily: FONTS.bodyBold,
        fontSize: 10,
        letterSpacing: 1.4,
        textTransform: "uppercase",
        color: WM,
        marginBottom: 6,
      }}
    >
      {label}
      {required ? <Text style={{ color: RED }}> *</Text> : null}
    </Text>
  );
}

function DarkInput({
  value,
  onChangeText,
  placeholder,
  multiline,
  numberOfLines,
  autoCapitalize,
  keyboardType,
}: {
  value: string;
  onChangeText: (t: string) => void;
  placeholder?: string;
  multiline?: boolean;
  numberOfLines?: number;
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  keyboardType?: "default" | "url" | "email-address";
}) {
  return (
    <TextInput
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor={WM}
      multiline={multiline}
      numberOfLines={numberOfLines}
      autoCapitalize={autoCapitalize}
      keyboardType={keyboardType}
      style={
        {
          backgroundColor: FOREST,
          borderWidth: 1,
          borderColor: BORDER,
          borderRadius: 6,
          paddingHorizontal: 12,
          paddingVertical: multiline ? 10 : 9,
          minHeight: multiline ? 96 : undefined,
          textAlignVertical: multiline ? "top" : "center",
          color: LINEN,
          fontFamily: FONTS.body,
          fontSize: 13,
        } as TextStyle
      }
    />
  );
}

function PrimaryButton({
  label,
  onPress,
  disabled,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <Pressable
      disabled={disabled}
      onPress={onPress}
      style={[
        {
          paddingHorizontal: 16,
          paddingVertical: 9,
          borderRadius: 6,
          backgroundColor: AMBER,
          opacity: disabled ? 0.5 : 1,
        } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: disabled ? "default" : "pointer" } as unknown as ViewStyle) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 12,
          letterSpacing: 0.6,
          color: THEME.accentText,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function SecondaryButton({
  label,
  onPress,
  disabled,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <Pressable
      disabled={disabled}
      onPress={onPress}
      style={[
        {
          paddingHorizontal: 16,
          paddingVertical: 9,
          borderRadius: 6,
          borderWidth: 1,
          borderColor: BORDER,
          backgroundColor: "transparent",
          opacity: disabled ? 0.5 : 1,
        } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: disabled ? "default" : "pointer" } as unknown as ViewStyle) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyMedium,
          fontSize: 12,
          letterSpacing: 0.6,
          color: LINEN,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function HelpRow() {
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as unknown as ViewStyle) : {};
  return (
    <View
      style={{
        backgroundColor: FOREST_MID,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: BORDER,
        padding: 18,
      }}
    >
      <Pressable
        onPress={() => router.push("/client/welcome" as Href)}
        style={[cursorWeb, { alignSelf: "flex-start" }]}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 12,
            letterSpacing: 0.6,
            color: AMBER,
          }}
        >
          {"Take the tour again →"}
        </Text>
      </Pressable>
      <Text
        style={{
          marginTop: 6,
          fontFamily: FONTS.body,
          fontSize: 12,
          lineHeight: 18,
          color: WM,
        }}
      >
        Replay the welcome screen with the seven-phase Coravu process map.
      </Text>
    </View>
  );
}
